<template>
  <transition name="fade">
    <div v-if="!connectionStatus" class="offline-overlay">
      <!-- Dark overlay -->
      <div class="offline-backdrop"></div>
      
      <!-- Warning Modal -->
      <div class="offline-modal">
        <!-- Animated Icon -->
        <div class="offline-icon-container">
          <div class="offline-icon pulse">
            <i class="fas fa-wifi"></i>
            <div class="slash"></div>
          </div>
        </div>
        
        <!-- Warning Text -->
        <h1 class="offline-title">
          {{ language === 'ar' ? 'النظام غير متصل' : 'System ist Offline' }}
        </h1>
        
        <p class="offline-subtitle">
          {{ language === 'ar' ? 'لا يوجد اتصال بالإنترنت' : 'Keine Internetverbindung' }}
        </p>
        
        <!-- Status Info -->
        <div class="offline-info">
          <div class="info-item">
            <i class="fas fa-exclamation-triangle text-yellow-500"></i>
            <span>
              {{ language === 'ar' 
                ? 'لن يتم تحديث الطلبات الجديدة' 
                : 'Neue Bestellungen werden nicht aktualisiert' 
              }}
            </span>
          </div>
          <div class="info-item">
            <i class="fas fa-database text-blue-500"></i>
            <span>
              {{ language === 'ar' 
                ? 'البيانات المحلية لا تزال متاحة' 
                : 'Lokale Daten sind weiterhin verfügbar' 
              }}
            </span>
          </div>
        </div>
        
        <!-- Loading Animation -->
        <div class="reconnecting">
          <div class="spinner"></div>
          <span>
            {{ language === 'ar' ? 'محاولة إعادة الاتصال...' : 'Verbindung wird wiederhergestellt...' }}
          </span>
        </div>
        
        <!-- Dismiss Button (optional - for testing) -->
        <button 
          v-if="allowDismiss"
          @click="dismiss" 
          class="dismiss-button"
        >
          {{ language === 'ar' ? 'العمل دون اتصال' : 'Offline arbeiten' }}
        </button>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'OfflineWarningModal',
  props: {
    allowDismiss: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dismissed: false
    };
  },
  computed: {
    ...mapState(['connectionStatus', 'language'])
  },
  methods: {
    dismiss() {
      this.dismissed = true;
      setTimeout(() => {
        this.dismissed = false;
      }, 30000); // Re-show after 30 seconds if still offline
    }
  }
};
</script>

<style scoped>
/* Overlay and Backdrop */
.offline-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.offline-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  backdrop-filter: none;
}

/* Modal Container */
.offline-modal {
  position: relative;
  background: linear-gradient(135deg, #1e1e2e 0%, #2a2a3e 100%);
  border-radius: var(--ods-radius-lg);
  padding: 3rem;
  max-width: 500px;
  width: 90%;
  text-align: center;
  box-shadow: 0 0 100px rgba(239, 68, 68, 0.5),
    0 0 50px rgba(239, 68, 68, 0.3),
    inset 0 0 30px rgba(239, 68, 68, 0.1);
  border: 2px solid rgba(239, 68, 68, 0.3);
  animation: shake 0.5s ease-in-out;
}

/* Icon Container */
.offline-icon-container {
  margin-bottom: 2rem;
}

.offline-icon {
  display: inline-block;
  position: relative;
  font-size: 5rem;
  color: #ef4444;
  animation: pulse 2s infinite;
}

.offline-icon .slash {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-45deg);
  width: 100%;
  height: 3px;
  background: #ef4444;
  box-shadow: 0 0 10px rgba(239, 68, 68, 0.8);
}

/* Title and Subtitle */
.offline-title {
  font-size: 2.5rem;
  font-weight: 900;
  color: var(--ods-ink);
  margin-bottom: 0.5rem;
  text-shadow: 0 0 20px rgba(239, 68, 68, 0.5);
}

.offline-subtitle {
  font-size: 1.25rem;
  color: #a0a0a0;
  margin-bottom: 2rem;
}

/* Info Items */
.offline-info {
  background: rgba(0, 0, 0, 0.3);
  border-radius: var(--ods-radius);
  padding: 1.5rem;
  margin-bottom: 2rem;
}

.info-item {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  color: #e0e0e0;
  font-size: 1rem;
}

.info-item:last-child {
  margin-bottom: 0;
}

.info-item i {
  font-size: 1.25rem;
}

/* Reconnecting Animation */
.reconnecting {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  color: var(--ods-accent-ink);
  font-size: 1rem;
  margin-bottom: 1.5rem;
}

.spinner {
  width: 24px;
  height: 24px;
  border: 3px solid rgba(96, 165, 250, 0.3);
  border-top-color: var(--ods-accent);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

/* Dismiss Button */
.dismiss-button {
  background: var(--ods-surface-3);
  color: var(--ods-ink);
  border: 1px solid var(--ods-line-2);
  padding: 0.75rem 2rem;
  border-radius: var(--ods-radius);
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.dismiss-button:hover {
  background: var(--ods-surface-3);
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}

/* Animations */
@keyframes fade-enter {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  10% { transform: translateX(-10px); }
  20% { transform: translateX(10px); }
  30% { transform: translateX(-10px); }
  40% { transform: translateX(10px); }
  50% { transform: translateX(-5px); }
  60% { transform: translateX(5px); }
  70% { transform: translateX(-2px); }
  80% { transform: translateX(2px); }
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.7;
  }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Transition Classes */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

/* Responsive Design */
@media (max-width: 640px) {
  .offline-modal {
    padding: 2rem;
    width: 95%;
  }
  
  .offline-title {
    font-size: 2rem;
  }
  
  .offline-icon {
    font-size: 4rem;
  }
}
</style>